import { Controller } from "@hotwired/stimulus"

// Keeps the guide's sticky header in step with the page: swaps the wordmark for the guide's title
// once the hero title scrolls under the header, docks search and filter buttons into the date
// strip once the search row does, highlights the day being read, and jumps to a day when its
// chip is tapped.
export default class extends Controller {
  static targets = ["header", "hero", "strip", "chip", "section", "searchRow"]
  static outlets = ["guide-rest"]

  connect() {
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.onScroll = this.onScroll.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll, { passive: true })
    this.sync()

    // A day picked further along the strip (a filtered list or map) starts in view
    const picked = this.chipTargets.find(chip => chip.getAttribute("aria-current") === "true")
    if (picked) this.revealChip(picked, "instant")
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onScroll)
    if (this.frame) cancelAnimationFrame(this.frame)
    clearTimeout(this.jumpTimer)
  }

  // Re-run after favourites or search hide sections: hide chips for empty days and refresh counts
  sync() {
    this.sectionTargets.forEach(section => {
      const cards = this.visibleCards(section)
      // Events still on from an earlier day don't count towards the day's schedule, and ongoing listings
      // have a count of their own (day_count_label). Rows still to be fetched count too (guide-rest).
      const rest = [...section.querySelectorAll("[data-guide-rest-target='placeholder']")].filter(placeholder => this.isVisible(placeholder))
      const restCount = kind => rest.reduce((sum, placeholder) => sum + Number(placeholder.dataset[kind] || 0), 0)
      const stillOnCards = cards.filter(card => card.hasAttribute("data-still-on")).length
      const ongoingCards = cards.filter(card => card.hasAttribute("data-ongoing")).length
      const stillOn = stillOnCards + restCount("stillOn")
      const ongoing = ongoingCards + restCount("ongoing")
      const scheduled = cards.length - stillOnCards - ongoingCards + restCount("scheduled")
      const count = section.querySelector("[data-day-count]")
      if (count) {
        const counts = []
        if (scheduled > 0) counts.push(`${scheduled} ${scheduled === 1 ? "event" : "events"}`)
        if (ongoing > 0) counts.push(`${ongoing} ongoing`)
        count.textContent = counts.length ? counts.join(", ") : stillOn > 0 ? `${stillOn} still on` : "0 events"
      }
    })

    this.chipTargets.forEach(chip => {
      const section = this.sectionFor(chip.dataset.day)
      if (section) chip.hidden = !this.isVisible(section)
    })

    // With every day hidden (nothing saved yet on Me) the strip would be an empty band. A strip that
    // carries the docked search button stays, so search is still to hand when nothing matches.
    const strip = this.hasStripTarget && this.stripTarget.closest(".day-strip")
    if (strip && this.chipTargets.length && !strip.querySelector(".strip-dock")) {
      strip.hidden = this.chipTargets.every(chip => chip.hidden)
    }

    this.currentDay = null
    this.update()
  }

  onScroll() {
    if (this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.update()
    })
  }

  update() {
    const headerBottom = this.hasHeaderTarget ? this.headerTarget.getBoundingClientRect().bottom : 0

    if (this.hasHeroTarget && this.hasHeaderTarget) {
      const heroVisible = this.heroTarget.getBoundingClientRect().bottom > headerBottom
      this.headerTarget.toggleAttribute("data-hero-visible", heroVisible)
    }

    if (this.hasSearchRowTarget && this.hasHeaderTarget) {
      const searchHidden = this.searchRowTarget.getBoundingClientRect().bottom <= headerBottom
      this.headerTarget.toggleAttribute("data-search-docked", searchHidden)
    }

    if (this.jumpingTo) return

    const sections = this.sectionTargets.filter(section => this.isVisible(section))
    if (!sections.length || !this.chipTargets.some(chip => this.sectionFor(chip.dataset.day))) return

    // The day being read is the last one whose heading has passed a line a little below the header
    const readingLine = headerBottom + Math.min(160, window.innerHeight * 0.25)
    let current = sections[0]
    for (const section of sections) {
      if (section.getBoundingClientRect().top <= readingLine) current = section
      else break
    }

    const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2
    if (atBottom && window.scrollY > 0) current = sections[sections.length - 1]

    this.setCurrent(current.dataset.day)
  }

  async jump(event) {
    const day = event.currentTarget.dataset.day
    const section = this.sectionFor(day)
    if (!section) return

    event.preventDefault()
    this.setCurrent(day)
    // Hold the highlight on the tapped day while the page scrolls past the days in between
    this.jumpingTo = day
    clearTimeout(this.jumpTimer)

    // The days in between are made whole first, so they don't grow under the scroll
    if (this.hasGuideRestOutlet && this.guideRestOutlet.pending) {
      await this.guideRestOutlet.load().catch(() => {})
      if (this.jumpingTo !== day) return
    }

    this.jumpTimer = setTimeout(() => {
      this.jumpingTo = null
      this.update()
    }, this.reduceMotion ? 50 : 900)

    section.scrollIntoView({ behavior: this.reduceMotion ? "auto" : "smooth", block: "start" })
  }

  // The docked search button: bring the search row back under the header and type straight away
  focusSearch() {
    if (!this.hasSearchRowTarget) return

    const input = this.searchRowTarget.querySelector("input")
    input?.focus({ preventScroll: true })
    this.searchRowTarget.scrollIntoView({ behavior: this.reduceMotion ? "auto" : "smooth", block: "start" })
  }

  setCurrent(day) {
    if (!day || day === this.currentDay) return
    this.currentDay = day

    this.chipTargets.forEach(chip => {
      if (!this.sectionFor(chip.dataset.day)) return

      if (chip.dataset.day === day) {
        chip.setAttribute("aria-current", "true")
        this.revealChip(chip)
      } else {
        chip.removeAttribute("aria-current")
      }
    })
  }

  revealChip(chip, behavior = this.reduceMotion ? "auto" : "smooth") {
    if (!this.hasStripTarget) return

    const strip = this.stripTarget
    const left = chip.offsetLeft - (strip.clientWidth - chip.offsetWidth) / 2
    strip.scrollTo({ left: Math.max(0, left), behavior })
  }

  sectionFor(day) {
    return this.sectionTargets.find(section => section.dataset.day === day)
  }

  visibleCards(section) {
    return [...section.querySelectorAll("[data-favourites-target='card']")].filter(card => this.isVisible(card))
  }

  isVisible(element) {
    return element.getClientRects().length > 0
  }
}
